import type { GetTokenSilentlyOptions, User } from "@auth0/auth0-spa-js";
import { Auth0Client } from "@auth0/auth0-spa-js";

import type { TPublicAuth0Config } from "../odin/apiODIN";

export type TUser = User;

export type TApiAuth = {
    initialize: (config: TPublicAuth0Config) => Promise<void>;
    getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
    handleRedirectCallback: () => Promise<string | undefined>;
    getUser: () => Promise<User | null>;
    loginWithRedirect: (returnTo?: string) => Promise<void>;
    logout: () => Promise<void>;
};

export const createApiAuth = ({
    loginCallbackPath,
    logoutReturnToPath,
}: {
    loginCallbackPath: string;
    logoutReturnToPath: string;
}): TApiAuth => {
    let auth0Client: Auth0Client | null = null;

    const getClientIfInitialized = () => {
        if (!auth0Client) throw new Error("Auth client not initialized");
        return auth0Client;
    };

    return {
        async initialize(config) {
            if (auth0Client) return;

            auth0Client = new Auth0Client({
                domain: config.domain,
                clientId: config.clientId,
                authorizationParams: {
                    audience: config.audience,
                    redirect_uri: loginCallbackPath,
                },
                useRefreshTokens: true,
                cacheLocation: "localstorage",
            });

            await Promise.resolve();
        },

        getTokenSilently: async options => {
            return getClientIfInitialized().getTokenSilently(options);
        },

        handleRedirectCallback: async () => {
            const result =
                await getClientIfInitialized().handleRedirectCallback<{
                    returnTo: string;
                }>();
            return result.appState?.returnTo;
        },

        getUser: async () => {
            return (await getClientIfInitialized().getUser()) || null;
        },

        loginWithRedirect: async returnTo => {
            return getClientIfInitialized().loginWithRedirect({
                appState: returnTo !== undefined ? { returnTo } : undefined,
            });
        },

        logout: async () => {
            return getClientIfInitialized().logout({
                logoutParams: {
                    returnTo: logoutReturnToPath,
                },
            });
        },
    };
};
